import React, { useContext, useState, useEffect, useRef } from 'react';
import { Box, Typography, Avatar, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LinkedIn, Language, Twitter, ChevronLeftOutlined, ChevronRightOutlined, RoomOutlined } from '@mui/icons-material';
import { PreviewCtx, DragCtx } from '../contexts';
import { getEffectiveStyle } from '../utils';
import { useSite } from '../../../context/SiteContext';

// ─── TextElement ──────────────────────────────────────────────────────────────
export function TextElement({ el }) {
  const bp = useContext(DragCtx)?.breakpointRef?.current || 'desktop';
  const cs = getEffectiveStyle(el, bp);

  return (
    <Box
      style={{ ...cs, display: 'block', minWidth: '40px', minHeight: '20px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      dangerouslySetInnerHTML={{ __html: el.content }}
    />
  );
}

// ─── ButtonElement ────────────────────────────────────────────────────────────
export function ButtonElement({ el }) {
  const bp = useContext(DragCtx)?.breakpointRef?.current || 'desktop';
  const cs = getEffectiveStyle(el, bp);

  return (
    <Box
      component="a"
      href={el.href || '#'}
      style={{ ...cs, display: 'inline-block', textDecoration: 'none' }}
    >
      {el.label}
    </Box>
  );
}

// ─── ImageElement ─────────────────────────────────────────────────────────────
export function ImageElement({ el }) {
  const bp = useContext(DragCtx)?.breakpointRef?.current || 'desktop';
  const cs = getEffectiveStyle(el, bp);

  if (!el.src) return null;

  return (
    <img
      src={el.src}
      alt={el.alt || ''}
      style={{ ...cs, display: 'block' }}
      draggable={false}
    />
  );
}

// ─── ContainerElement ─────────────────────────────────────────────────────────
export function ContainerElement({ el, selectedId, onSelectChild, onUpdateEl, onDeleteEl,
  onMoveChildToSection, onMoveChildToContainer, onInsertNewChildAt,
  onMoveTopLevelIntoContainer, onSplitDropIntoEmpty, sectionId }) {
  const ctx         = useContext(DragCtx);
  const bp          = ctx?.breakpointRef?.current || 'desktop';
  const cs          = getEffectiveStyle(el, bp);
  const orientation = cs.flexDirection || el.orientation || 'column';
  const isRow       = orientation === 'row';

  const children = el.children || [];

  const containerStyle = (() => {
    const { overlayBackground, ...restStyle } = cs;
    const bgUrl = (cs.background || '').match(/url\(['"']?([^'"()]+)['"']?\)/)?.[1];
    const combined = overlayBackground && bgUrl
      ? { ...restStyle, backgroundImage: `${overlayBackground}, url('${bgUrl}')`, backgroundSize: cs.backgroundSize || 'cover', backgroundPosition: cs.backgroundPosition || 'center', backgroundRepeat: 'no-repeat' }
      : restStyle;
    return { ...combined, display: 'flex', flexDirection: 'column', overflow: 'visible' };
  })();

  const NOOP = () => {};

  return (
    <Box style={containerStyle}>
      {children.length > 0 && (
        <Box sx={{
          display: 'flex',
          flexDirection: orientation,
          flexWrap: 'nowrap',
          overflow: 'visible',
          justifyContent: isRow ? (cs.alignItems || 'stretch') : (cs.justifyContent || 'flex-start'),
          alignItems: !isRow ? (cs.alignItems || 'stretch') : undefined,
          gap: cs.gap || '12px',
        }}>
          {children.map((child) => (
            <Box
              key={child.id}
              sx={{ flex: isRow ? '1 1 0' : undefined, minWidth: isRow ? 0 : undefined, display: 'flex', flexDirection: 'column', justifyContent: isRow ? (cs.justifyContent || 'flex-start') : undefined, overflow: 'visible' }}
            >
              <AnyElement
                el={child}
                selected={false}
                onSelect={NOOP}
                onChange={NOOP}
                onDragStart={NOOP}
                isDragOver={false}
                selectedId={null}
                onSelectChild={NOOP}
                onUpdateEl={NOOP}
                onDeleteEl={NOOP}
                onMoveChildToSection={NOOP}
                onMoveChildToContainer={NOOP}
                onInsertNewChildAt={NOOP}
                onMoveTopLevelIntoContainer={NOOP}
                onSplitDropIntoEmpty={NOOP}
                sectionId={sectionId}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── CountdownElement ─────────────────────────────────────────────────────────
export function CountdownElement({ el }) {
  const [timeLeft, setTimeLeft] = useState({});

  useEffect(() => {
    const calc = () => {
      const target = new Date(el.targetDate).getTime();
      const diff   = Math.max(0, target - Date.now());
      setTimeLeft({
        days:  Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        mins:  Math.floor((diff % 3600000)  / 60000),
        secs:  Math.floor((diff % 60000)    / 1000),
      });
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [el.targetDate]);

  const show   = el.show   || { days: true, hours: true, mins: true, secs: true };
  const layout = el.layout || '2x2';

  const units = [
    { key: 'days',  label: 'Days',  value: timeLeft.days  ?? 0 },
    { key: 'hours', label: 'Hours', value: timeLeft.hours ?? 0 },
    { key: 'mins',  label: 'Mins',  value: timeLeft.mins  ?? 0 },
    { key: 'secs',  label: 'Sec',   value: timeLeft.secs  ?? 0 },
  ].filter(u => show[u.key]);

  const pad = n => String(n).padStart(2, '0');

  const CardItem = ({ unit }) => (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: el.cardBackground || '#f1f5f9',
      borderRadius: el.cardRadius   || '12px',
      width:  '100%',
      height: el.cardHeight || '100px',
    }}>
      <Typography sx={{ fontSize: el.numberSize || '48px', fontWeight: 700, color: el.numberColor || '#1a1a2e', lineHeight: 1 }}>
        {pad(unit.value)}
      </Typography>
      <Typography sx={{ fontSize: el.labelSize || '13px', color: el.labelColor || '#7a8494', mt: 0.5, fontWeight: 500 }}>
        {unit.label}
      </Typography>
    </Box>
  );

  return layout === '2x2' ? (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', p: '4px' }}>
      {units.map(u => <CardItem key={u.key} unit={u} />)}
    </Box>
  ) : (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '12px', p: '4px' }}>
      {units.map(u => <Box key={u.key} sx={{ flex: 1 }}><CardItem unit={u} /></Box>)}
    </Box>
  );
}


// ─── Speakers helpers ─────────────────────────────────────────────────────────
function getSpkProp(el, bp, key) {
  if (!bp || bp === 'desktop') return el[key];
  return el.responsive?.[bp]?.[key] ?? el[key];
}

function spkInitials(name = '') { return name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase(); }
function spkHue(name = '') { let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360; return h; }

function SocialLinks({ speaker, size = 21, dark = false }) {
  const links = [
    speaker.socialLinks?.linkedin && { icon: <LinkedIn sx={{ fontSize: size }} />, url: speaker.socialLinks.linkedin },
    speaker.socialLinks?.twitter  && { icon: <Twitter  sx={{ fontSize: size }} />, url: speaker.socialLinks.twitter },
    speaker.socialLinks?.website  && { icon: <Language  sx={{ fontSize: size }} />, url: speaker.socialLinks.website },
  ].filter(Boolean);
  if (!links.length) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
      {links.map((l, i) => (
        <Box key={i} onClick={e => { e.stopPropagation(); window.open(l.url, '_blank', 'noopener'); }}
          sx={{ width: size + 10, height: size + 10, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s',
            ...(dark ? { background: alpha('#000', 0.07), color: '#5a6474', '&:hover': { background: alpha('#4f46e5', 0.12), color: '#4f46e5' } }
                     : { background: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.82)', '&:hover': { background: 'rgba(255,255,255,0.28)', color: '#fff' } }) }}>
          {l.icon}
        </Box>
      ))}
    </Box>
  );
}

function PhotoPanel({ speaker, cardRadius }) {
  const hue = spkHue(speaker.name); const initials = spkInitials(speaker.name);
  return (
    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '3.5/4', borderRadius: cardRadius, overflow: 'hidden', flexShrink: 0 }}>
      {speaker.avatar
        ? <Box component="img" src={speaker.avatar} alt={speaker.name} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        : <Box sx={{ position: 'absolute', inset: 0, background: `linear-gradient(145deg, hsl(${hue},60%,92%) 0%, hsl(${hue},45%,80%) 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Avatar sx={{ width: 72, height: 72, fontSize: '1.6rem', fontWeight: 700, background: `hsl(${hue},58%,46%)`, border: '3px solid rgba(255,255,255,0.7)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>{initials}</Avatar>
          </Box>
      }
    </Box>
  );
}

function CardT1({ speaker, cardProps }) {
  const [hovered, setHovered] = useState(false);
  const { nameFontSize, titleFontSize, cardRadius, textAlign = 'left' } = cardProps;
  const hasBio = speaker.bio || speaker.socialLinks?.linkedin || speaker.socialLinks?.twitter || speaker.socialLinks?.website;
  return (
    <Box onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      sx={{ position: 'relative', width: '100%', aspectRatio: '3.5/4', borderRadius: cardRadius, overflow: 'hidden', flexShrink: 0, cursor: 'default' }}>
      <PhotoPanel speaker={speaker} cardRadius={cardRadius} />
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%', background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.18) 70%, transparent 100%)', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: '10px 12px', transform: hovered && hasBio ? 'translateY(110%)' : 'translateY(0)', transition: 'transform 0.28s ease', textAlign }}>
        <Typography sx={{ fontSize: nameFontSize, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{speaker.name}</Typography>
        {(speaker.title || speaker.company) && <Typography sx={{ fontSize: titleFontSize, color: 'rgba(255,255,255,0.75)', mt: 0.25 }}>{[speaker.title, speaker.company].filter(Boolean).join(' · ')}</Typography>}
      </Box>
      {hasBio && (
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, rgba(10,20,40,0.93) 0%, rgba(20,40,80,0.89) 100%)', backdropFilter: 'blur(2px)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', p: '14px 14px 12px', opacity: hovered ? 1 : 0, transform: hovered ? 'translateY(0)' : 'translateY(10px)', transition: 'opacity 0.25s ease, transform 0.25s ease', pointerEvents: hovered ? 'auto' : 'none' }}>
          <Typography sx={{ fontSize: nameFontSize, fontWeight: 700, color: '#fff', mb: 0.4, textAlign }}>{speaker.name}</Typography>
          {(speaker.title || speaker.company) && <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', mb: 0.75, textAlign }}>{[speaker.title, speaker.company].filter(Boolean).join(' · ')}</Typography>}
          {speaker.bio && <Typography sx={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.82)', lineHeight: 1.55, mb: 1, textAlign, display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{speaker.bio}</Typography>}
          <Box sx={{ display: 'flex', justifyContent: textAlign === 'right' ? 'flex-end' : textAlign === 'center' ? 'center' : 'flex-start' }}><SocialLinks speaker={speaker} /></Box>
        </Box>
      )}
    </Box>
  );
}

function CardT2({ speaker, cardProps }) {
  const [hovered, setHovered] = useState(false);
  const { nameFontSize, titleFontSize, nameColor, titleColor, cardRadius, textAlign = 'left' } = cardProps;
  const hasSocial = speaker.socialLinks?.linkedin || speaker.socialLinks?.twitter || speaker.socialLinks?.website;
  return (
    <Box onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      sx={{ width: '100%', display: 'flex', flexDirection: 'column', cursor: 'default', transform: hovered ? 'scale(0.97)' : 'scale(1)', transition: 'transform 0.22s ease', transformOrigin: 'center bottom' }}>
      <PhotoPanel speaker={speaker} cardRadius={cardRadius} />
      <Box sx={{ pt: 1.25, px: 0.25, textAlign }}>
        <Typography sx={{ fontSize: nameFontSize, fontWeight: 700, color: nameColor, lineHeight: 1.2 }}>{speaker.name}</Typography>
        {(speaker.title || speaker.company) && <Typography sx={{ fontSize: titleFontSize, color: titleColor, mt: 0.25 }}>{[speaker.title, speaker.company].filter(Boolean).join(' · ')}</Typography>}
        {speaker.bio && <Typography sx={{ fontSize: '11.5px', color: alpha(titleColor, 0.85), lineHeight: 1.55, mt: 0.75, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{speaker.bio}</Typography>}
        {hasSocial && <Box sx={{ mt: 0.75, display: 'flex', justifyContent: textAlign === 'right' ? 'flex-end' : textAlign === 'center' ? 'center' : 'flex-start' }}><SocialLinks speaker={speaker} dark /></Box>}
      </Box>
    </Box>
  );
}

function CardT3({ speaker, cardProps }) {
  const [hovered, setHovered] = useState(false);
  const { nameFontSize, titleFontSize, nameColor, titleColor, textAlign = 'left' } = cardProps;
  const hue = spkHue(speaker.name); const initials = spkInitials(speaker.name);
  const hasSocial = speaker.socialLinks?.linkedin || speaker.socialLinks?.twitter || speaker.socialLinks?.website;
  const alignItems = textAlign === 'right' ? 'flex-end' : textAlign === 'center' ? 'center' : 'flex-start';
  return (
    <Box onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'default', transform: hovered ? 'scale(0.97)' : 'scale(1)', transition: 'transform 0.22s ease', transformOrigin: 'center bottom' }}>
      <Box sx={{ position: 'relative', width: '100%', paddingBottom: '100%', flexShrink: 0 }}>
        <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', overflow: 'hidden', background: speaker.avatar ? 'none' : `linear-gradient(145deg, hsl(${hue},60%,92%) 0%, hsl(${hue},45%,80%) 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `3px solid ${alpha('#fff', 0.85)}`, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
          {speaker.avatar
            ? <Box component="img" src={speaker.avatar} alt={speaker.name} sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            : <Avatar sx={{ width: '55%', height: '55%', fontSize: '1.6rem', fontWeight: 700, background: `hsl(${hue},58%,46%)` }}>{initials}</Avatar>
          }
        </Box>
      </Box>
      <Box sx={{ pt: 1.25, textAlign, px: 0.5, width: '100%' }}>
        <Typography sx={{ fontSize: nameFontSize, fontWeight: 700, color: nameColor, lineHeight: 1.2 }}>{speaker.name}</Typography>
        {(speaker.title || speaker.company) && <Typography sx={{ fontSize: titleFontSize, color: titleColor, mt: 0.25 }}>{[speaker.title, speaker.company].filter(Boolean).join(' · ')}</Typography>}
        {speaker.bio && <Typography sx={{ fontSize: '11.5px', color: alpha(titleColor, 0.85), lineHeight: 1.55, mt: 0.75, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{speaker.bio}</Typography>}
        {hasSocial && <Box sx={{ mt: 0.75, display: 'flex', justifyContent: alignItems }}><SocialLinks speaker={speaker} dark /></Box>}
      </Box>
    </Box>
  );
}

function CarouselTrack({ pages, page, perSlide, gap, CardComponent, cardProps }) {
  return (
    <Box sx={{ overflow: 'hidden', width: '100%' }}>
      <Box sx={{ display: 'flex', gap, transform: `translateX(calc(-${page * 100}% - ${page} * ${gap}))`, transition: 'transform 0.38s cubic-bezier(0.4, 0, 0.2, 1)' }}>
        {pages.map((group, gi) => (
          <Box key={gi} sx={{ display: 'grid', gridTemplateColumns: `repeat(${perSlide}, 1fr)`, gap, minWidth: '100%', flexShrink: 0 }}>
            {Array.from({ length: perSlide }).map((_, ci) => (
              <Box key={ci}>{group[ci] ? <CardComponent speaker={group[ci]} cardProps={cardProps} /> : null}</Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function SpeakersElement({ el }) {
  const { event } = useSite();
  const bp = useContext(DragCtx)?.breakpointRef?.current || 'desktop';

  const displayMode = el.displayMode || 'carousel';
  const template    = el.template    || 1;
  const perSlide    = Math.max(1, Math.min(10, getSpkProp(el, bp, 'perSlide') ?? 4));
  const columns     = Math.max(1, getSpkProp(el, bp, 'columns')  ?? 4);
  const gap         = getSpkProp(el, bp, 'gap')         || '16px';
  const gridJustify = getSpkProp(el, bp, 'gridJustify') || 'center';
  const autoScroll  = el.autoScroll  === true;
  const speakers    = event?.speakers || [];

  const cardProps = {
    nameColor:    el.nameColor    || '#1a1a2e',
    titleColor:   el.titleColor   || '#5a6474',
    nameFontSize: el.nameFontSize || '15px',
    titleFontSize:el.titleFontSize|| '13px',
    cardRadius:   el.cardRadius   || '12px',
    textAlign:    el.textAlign    || 'left',
  };

  const CardComponent = template === 3 ? CardT3 : template === 2 ? CardT2 : CardT1;
  const pages = [];
  for (let i = 0; i < speakers.length; i += perSlide) pages.push(speakers.slice(i, i + perSlide));
  const totalPages = Math.max(1, pages.length);

  const [page, setPage] = useState(0);
  const autoRef = useRef(null);

  useEffect(() => { setPage(p => Math.min(p, totalPages - 1)); }, [totalPages]);

  useEffect(() => {
    clearInterval(autoRef.current);
    if (!autoScroll || displayMode !== 'carousel' || totalPages <= 1) return;
    autoRef.current = setInterval(() => setPage(p => (p + 1) % totalPages), 3000);
    return () => clearInterval(autoRef.current);
  }, [autoScroll, displayMode, totalPages]);

  const goTo = (idx) => {
    clearInterval(autoRef.current);
    setPage(Math.max(0, Math.min(totalPages - 1, idx)));
    if (autoScroll && totalPages > 1) autoRef.current = setInterval(() => setPage(p => (p + 1) % totalPages), 3000);
  };

  if (!speakers.length) return null;

  if (displayMode === 'carousel') return (
    <Box>
      <CarouselTrack pages={pages} page={page} perSlide={perSlide} gap={gap} CardComponent={CardComponent} cardProps={cardProps} />
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mt: 2 }}>
          <IconButton size="small" onClick={() => goTo(page - 1)} disabled={page === 0}
            sx={{ border: '1px solid #e4e9f0', background: '#fff', '&:hover': { background: 'rgba(79,70,229,0.07)' } }}>
            <ChevronLeftOutlined sx={{ fontSize: 18 }} />
          </IconButton>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
            {Array.from({ length: totalPages }).map((_, i) => (
              <Box key={i} onClick={() => goTo(i)}
                sx={{ width: i === page ? 22 : 7, height: 7, borderRadius: '4px', cursor: 'pointer', transition: 'all 0.25s ease', background: i === page ? '#4f46e5' : '#d1d9e0' }} />
            ))}
          </Box>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ea8b3', minWidth: 36, textAlign: 'center' }}>{page + 1} / {totalPages}</Typography>
          <IconButton size="small" onClick={() => goTo(page + 1)} disabled={page === totalPages - 1}
            sx={{ border: '1px solid #e4e9f0', background: '#fff', '&:hover': { background: 'rgba(79,70,229,0.07)' } }}>
            <ChevronRightOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap, justifyContent: gridJustify }}>
      {speakers.map(s => (
        <Box key={s.id || s._id || s.name} sx={{ width: `calc((100% - (${columns} - 1) * ${gap}) / ${columns})`, minWidth: 0 }}>
          <CardComponent speaker={s} cardProps={cardProps} />
        </Box>
      ))}
    </Box>
  );
}

// ─── Schedule helpers ─────────────────────────────────────────────────────────
function getSchedProp(el, bp, key) {
  if (!bp || bp === 'desktop') return el[key];
  return el.responsive?.[bp]?.[key] ?? el[key];
}

function to12h(time) {
  if (!time) return '';
  const [hStr, mStr] = time.split(':');
  let h = parseInt(hStr, 10);
  const m = mStr || '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

function formatDayLabel(label) {
  if (!label) return null;
  const iso = label.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(`${iso[1]}-${iso[2]}-${iso[3]}T00:00:00`);
    if (!isNaN(d)) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return label;
}

function groupByDayLabel(schedule) {
  const map = new Map();
  for (const item of schedule) {
    const key = item.dayLabel || `day-${item.dayNumber || 1}`;
    if (!map.has(key)) map.set(key, { key, tabIndex: map.size + 1, dayLabel: item.dayLabel || null, items: [] });
    map.get(key).items.push(item);
  }
  const groups = Array.from(map.values());
  for (const g of groups) {
    g.items.sort((a, b) =>
      (a.startTime || '').localeCompare(b.startTime || '') ||
      (a.order || 0) - (b.order || 0)
    );
  }
  return groups;
}

function schedGetHue(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
}
function schedGetInitials(name = '') {
  return name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function resolveSpeaker(speakerRef, allSpeakers) {
  if (speakerRef && typeof speakerRef === 'object') return speakerRef;
  return allSpeakers.find(
    s => s.id === speakerRef || s._id === speakerRef || s.name === speakerRef
  );
}

function SchedSpeakerCircle({ speakerRef, allSpeakers }) {
  const speaker  = resolveSpeaker(speakerRef, allSpeakers);
  const name     = speaker?.name || (typeof speakerRef === 'string' ? speakerRef : '') || '?';
  const hue      = schedGetHue(name);
  const initials = schedGetInitials(name);

  return speaker?.avatar ? (
    <Box
      component="img" src={speaker.avatar} alt={name} title={name}
      sx={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover',
        border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.12)', display: 'block',
        cursor: 'default', flexShrink: 0 }}
    />
  ) : (
    <Avatar title={name} sx={{
      width: 36, height: 36, fontSize: '0.75rem', fontWeight: 700,
      background: `hsl(${hue},58%,46%)`,
      border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
      cursor: 'default', flexShrink: 0,
    }}>
      {initials}
    </Avatar>
  );
}

function ScheduleRow({ item, el, allSpeakers, bannerUrl, isLast, bp }) {
  const isMobile = bp === 'mobile';

  // All props resolved through responsive overrides — same pattern as SpeakersElement
  const primaryColor    = getSchedProp(el, bp, 'primaryColor')    || '#1075BA';
  const secondaryColor  = getSchedProp(el, bp, 'secondaryColor')  || '#6b7280';
  const dividerColor    = getSchedProp(el, bp, 'dividerColor')    || '#f0f4f8';
  const timeFontSize    = getSchedProp(el, bp, 'timeFontSize')    || (isMobile ? '16px' : '22px');
  const titleFontSize   = getSchedProp(el, bp, 'titleFontSize')   || (isMobile ? '15px' : '16px');
  const descFontSize    = getSchedProp(el, bp, 'descFontSize')    || '13px';
  const showSpeakers    = getSchedProp(el, bp, 'showSpeakers')    !== false;
  const showBanner      = getSchedProp(el, bp, 'showBanner')      !== false;
  const showDescription = getSchedProp(el, bp, 'showDescription') !== false;
  const showLocation    = getSchedProp(el, bp, 'showLocation')    !== false;

  const speakerRefs   = item.speakers || [];
  const formattedDate = formatDayLabel(item.dayLabel);
  const MAX_VISIBLE   = 4;
  const visibleRefs   = speakerRefs.slice(0, MAX_VISIBLE);
  const overflow      = speakerRefs.length - MAX_VISIBLE;

  // Banner — full-width on mobile, thumbnail on desktop
  const bannerImg = showBanner && (item.posterUrl || bannerUrl) ? (
    isMobile ? (
      <Box component="img" src={item.posterUrl || bannerUrl} alt=""
        sx={{ width: '100%', aspectRatio: '96/72', objectFit: 'cover', borderRadius: '8px', display: 'block', mb: 1 }} />
    ) : (
      <Box component="img" src={item.posterUrl || bannerUrl} alt=""
        sx={{ width: 96, height: 72, objectFit: 'cover', borderRadius: '8px', flexShrink: 0, display: 'block' }} />
    )
  ) : null;

  // Speaker row — identical between layouts
  const speakerBlock = showSpeakers && speakerRefs.length > 0 ? (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {visibleRefs.map((ref, i) => (
        <Box key={i} sx={{ ml: i > 0 ? '-10px' : 0, zIndex: MAX_VISIBLE - i }}>
          <SchedSpeakerCircle speakerRef={ref} allSpeakers={allSpeakers} />
        </Box>
      ))}
      {overflow > 0 && (
        <Typography sx={{ ml: 1, fontSize: '12px', fontWeight: 600, color: secondaryColor, whiteSpace: 'nowrap', flexShrink: 0 }}>
          and {overflow} other{overflow > 1 ? 's' : ''}
        </Typography>
      )}
    </Box>
  ) : null;

  // ── Mobile: single-column stacked layout ─────────────────────────────────────
  if (isMobile) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column',
        py: 2.5, borderBottom: isLast ? 'none' : `1px solid ${dividerColor}`,
      }}>
        {/* Time + date inline above title */}
        {(item.startTime || formattedDate) && (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
            {item.startTime && (
              <Typography sx={{ fontSize: timeFontSize, fontWeight: 800, color: primaryColor, lineHeight: 1 }}>
                {to12h(item.startTime)}
              </Typography>
            )}
            {formattedDate && (
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: secondaryColor, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {formattedDate}
              </Typography>
            )}
          </Box>
        )}
        {/* Title — full width */}
        <Typography sx={{ fontSize: titleFontSize, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.3, mb: 1 }}>
          {item.title}
        </Typography>
        {/* Banner — full width below title */}
        {bannerImg}
        {/* Description */}
        {showDescription && item.description && (
          <Typography sx={{ fontSize: descFontSize, color: secondaryColor, lineHeight: 1.6, mb: 0.75 }}>
            {item.description}
          </Typography>
        )}
        {/* Location */}
        {showLocation && item.location && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.75 }}>
            <RoomOutlined sx={{ fontSize: 13, color: alpha(secondaryColor, 0.65), flexShrink: 0 }} />
            <Typography sx={{ fontSize: '11.5px', color: alpha(secondaryColor, 0.75), fontStyle: 'italic' }}>
              {item.location}
            </Typography>
          </Box>
        )}
        {/* Speakers */}
        {speakerBlock}
      </Box>
    );
  }

  // ── Desktop: 2-column grid (time left | content right) ───────────────────────
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: '110px 1fr',
      gap: '20px',
      alignItems: 'flex-start',
      py: 3,
      borderBottom: isLast ? 'none' : `1px solid ${dividerColor}`,
    }}>
      {/* Left: time + date */}
      <Box sx={{ flexShrink: 0 }}>
        {item.startTime && (
          <Typography sx={{ fontSize: timeFontSize, fontWeight: 800, color: primaryColor, lineHeight: 1 }}>
            {to12h(item.startTime)}
          </Typography>
        )}
        {formattedDate && (
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: secondaryColor, mt: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {formattedDate}
          </Typography>
        )}
      </Box>
      {/* Right: optional banner thumbnail + text block */}
      <Box sx={{ minWidth: 0, display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
        {bannerImg}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: titleFontSize, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.3, mb: item.description ? 0.5 : 0 }}>
            {item.title}
          </Typography>
          {showDescription && item.description && (
            <Typography sx={{ fontSize: descFontSize, color: secondaryColor, lineHeight: 1.6 }}>
              {item.description}
            </Typography>
          )}
          {showLocation && item.location && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.5 }}>
              <RoomOutlined sx={{ fontSize: 13, color: alpha(secondaryColor, 0.65), flexShrink: 0 }} />
              <Typography sx={{ fontSize: '11.5px', color: alpha(secondaryColor, 0.75), fontStyle: 'italic' }}>
                {item.location}
              </Typography>
            </Box>
          )}
        </Box>
        {speakerBlock && <Box sx={{ mt: 1 }}>{speakerBlock}</Box>}
      </Box>
    </Box>
  );
}

function SchedDayTab({ group, active, onClick, totalTabs, primaryColor = '#0f766e', isMobile = false }) {
  const dateFormatted = formatDayLabel(group.dayLabel);
  const label = dateFormatted
    ? `Day ${group.tabIndex} (${dateFormatted})`
    : `Day ${group.tabIndex}`;

  return (
    <Box
      onClick={onClick}
      sx={{
        flex: isMobile ? '0 0 auto' : 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        py: 1.5, px: isMobile ? 2 : 1.5, cursor: 'pointer',
        borderBottom: active ? `3px solid ${primaryColor}` : '3px solid transparent',
        background: active ? alpha(primaryColor, 0.04) : 'transparent',
        transition: 'all 0.18s',
        '&:hover': { background: alpha(primaryColor, 0.06) },
      }}
    >
      <Typography sx={{
        fontSize: totalTabs > 3 ? '0.85rem' : '1rem',
        fontWeight: 700,
        color: active ? primaryColor : '#5a6474',
        textAlign: 'center', whiteSpace: 'nowrap',
        transition: 'color 0.18s',
      }}>
        {label}
      </Typography>
    </Box>
  );
}

export function ScheduleElement({ el }) {
  const { event } = useSite();
  const bp        = useContext(DragCtx)?.breakpointRef?.current || 'desktop';
  const isMobile  = bp === 'mobile';

  const schedule  = event?.schedule  || [];
  const speakers  = event?.speakers  || [];
  const bannerUrl = event?.bannerUrl || '';

  const groups   = groupByDayLabel(schedule);
  const multiDay = groups.length > 1;

  const [activeKey, setActiveKey] = useState(() => groups[0]?.key || '');

  // Keep activeKey in sync if schedule changes (e.g. first render after data loads)
  useEffect(() => {
    setActiveKey(k => {
      if (groups.find(g => g.key === k)) return k;
      return groups[0]?.key || '';
    });
  }, [schedule]);

  const currentItems = (groups.find(g => g.key === activeKey) || groups[0])?.items || [];
  const primaryColor = getSchedProp(el, bp, 'primaryColor') || '#0f766e';
  const dividerColor = getSchedProp(el, bp, 'dividerColor') || '#f0f4f8';

  if (!schedule.length) return null;

  return (
    <Box>
      {multiDay && (
        <Box sx={{
          display: 'flex', width: '100%',
          borderBottom: `2px solid ${dividerColor}`,
          overflowX: isMobile ? 'auto' : 'visible',
          '&::-webkit-scrollbar': { height: 0 },
          scrollbarWidth: 'none',
        }}>
          {groups.map(group => (
            <SchedDayTab
              key={group.key}
              group={group}
              active={activeKey === group.key}
              onClick={() => setActiveKey(group.key)}
              totalTabs={groups.length}
              primaryColor={primaryColor}
              isMobile={isMobile}
            />
          ))}
        </Box>
      )}
      <Box>
        {currentItems.map((item, idx) => (
          <ScheduleRow
            key={item.id || idx}
            item={item}
            el={el}
            allSpeakers={speakers}
            bannerUrl={bannerUrl}
            isLast={idx === currentItems.length - 1}
            bp={bp}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── AnyElement ───────────────────────────────────────────────────────────────
export function AnyElement({ el, sectionId, selectedId,
  onSelectChild, onUpdateEl, onDeleteEl,
  onMoveChildToSection, onMoveChildToContainer,
  onInsertNewChildAt, onMoveTopLevelIntoContainer, onSplitDropIntoEmpty,
}) {
  const bp = useContext(DragCtx)?.breakpointRef?.current || 'desktop';
  const isPreview = useContext(PreviewCtx);

  const vis = el.visibleOn || {};
  const isVisible = vis[bp] !== false;

  if (isPreview && !isVisible) return null;

  if (el.type === 'text')      return <TextElement el={el} />;
  if (el.type === 'button')    return <ButtonElement el={el} />;
  if (el.type === 'image')     return <ImageElement el={el} />;
  if (el.type === 'countdown') return <CountdownElement el={el} />;
  if (el.type === 'speakers')  return <SpeakersElement  el={el} />;
  if (el.type === 'schedule')  return <ScheduleElement  el={el} />;
  if (el.type === 'container') return (
    <ContainerElement
      el={el}
      selectedId={null}
      onSelectChild={() => {}}
      onUpdateEl={() => {}}
      onDeleteEl={() => {}}
      onMoveChildToSection={() => {}}
      onMoveChildToContainer={() => {}}
      onInsertNewChildAt={() => {}}
      onMoveTopLevelIntoContainer={() => {}}
      onSplitDropIntoEmpty={() => {}}
      sectionId={sectionId}
    />
  );

  return null;
}